import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';
import { User } from '../types';
import { getUserDocument, migrateUserStats } from '../services/auth.service';

interface AuthContextType {
  currentUser: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  firebaseUser: null,
  loading: true,
  error: null,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        setFirebaseUser(user);
        setError(null);

        if (user) {
          try {
            // Run migration for existing users (automatically adds multiplayerStats if missing)
            await migrateUserStats(user.uid);

            // Fetch user document from Firestore
            const userDoc = await getUserDocument(user.uid);
            setCurrentUser(userDoc);
          } catch (err: any) {
            console.error('Error fetching user document:', err);
            setError(err.message || 'Failed to load user data');
            setCurrentUser(null);
          }
        } else {
          setCurrentUser(null);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Auth state change error:', err);
        setError(err.message || 'Authentication error');
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    firebaseUser,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
